import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthAPI } from '@certi/shared/api/auth/AuthAPI'
import type { UserProfileResponse } from '@certi/shared/types'
import { decodeJwtPayload } from '@certi/shared/api/auth/jwt-utils'
import type { Session, User } from '@/types/auth'
import { zustandStorage } from '@certi/shared/utils/storage'

type AuthState = {
  user?: User
  session?: Session
  accessToken?: string
  loading: boolean
  error?: string
  isHydrated: boolean

  // Actions
  signIn: (params: {
    email: string
    password: string
    remember?: boolean
  }) => Promise<void>
  signUp: (params: {
    firstName: string
    lastName: string
    email: string
    password: string
  }) => Promise<void>
  signInWithGoogle: (idToken: string) => Promise<void>
  signInWithApple: (params: {
    identityToken: string
    user?: { name?: { firstName?: string; lastName?: string } }
  }) => Promise<void>
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
  refreshUser: () => Promise<void>
  syncUserProfile: (profile: UserProfileResponse) => void
  clearError: () => void

  // Dev only
  devLogin: () => void

  // Getters
  isAdmin: () => boolean

  // Internal
  completeSignIn: (session: Session) => Promise<void>
  _hasHydrated: boolean
  _setHasHydrated: (state: boolean) => void
}

const STORAGE_KEY = 'certi-auth-storage'

const persistAccessToken = (accessToken: string) => {
  try { localStorage.setItem('accessToken', accessToken) } catch {}
}

const clearPersistedAccessToken = () => {
  try { localStorage.removeItem('accessToken') } catch {}
}

const buildFallbackUserFromAccessToken = (accessToken: string): User | undefined => {
  const payload = decodeJwtPayload(accessToken)
  if (!payload?.sub) {
    return undefined
  }

  return {
    id: payload.sub,
    email: '',
    status: 'ACTIVE',
    roles: payload.roles ?? [],
    createdAt: '',
  }
}

const mergeUserWithProfile = (
  existingUser: User | undefined,
  profile: UserProfileResponse
): User | undefined => {
  if (!existingUser) {
    return undefined
  }

  return {
    ...existingUser,
    id: profile.id,
    email: profile.email,
    emailVerified: profile.emailVerified,
    firstName: profile.firstName,
    lastName: profile.lastName,
    phone: profile.phone,
    phoneVerified: profile.phoneVerified,
    status: profile.status,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: undefined,
      session: undefined,
      loading: false,
      error: undefined,
      isHydrated: false,
      _hasHydrated: false,

      completeSignIn: async (session: Session) => {
        const fallbackUser = buildFallbackUserFromAccessToken(session.accessToken)
        const sessionWithUser = { ...session, user: fallbackUser }

        set({
          user: fallbackUser,
          session: sessionWithUser,
          accessToken: session.accessToken,
          loading: false,
          error: undefined,
        })

        persistAccessToken(session.accessToken)

        void hydrateAuthenticatedUser(session.accessToken)
      },

      signIn: async ({ email, password, remember: _remember = false }) => {
        set({ loading: true, error: undefined })
        try {
          const session = await AuthAPI.signIn(email, password)
          await get().completeSignIn(session)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed'
          set({
            loading: false,
            error: message,
            user: undefined,
            session: undefined,
            accessToken: undefined,
          })
          throw error
        }
      },

      signUp: async ({ firstName, lastName, email, password }) => {
        set({ loading: true, error: undefined })
        try {
          const session = await AuthAPI.signUp(firstName, lastName, email, password)
          await get().completeSignIn(session)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          set({
            loading: false,
            error: message,
            user: undefined,
            session: undefined,
            accessToken: undefined,
          })
          throw error
        }
      },

      signInWithGoogle: async (idToken) => {
        set({ loading: true, error: undefined })
        try {
          const session = await AuthAPI.signInWithGoogle(idToken)
          await get().completeSignIn(session)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Google sign in failed'
          set({
            loading: false,
            error: message,
            user: undefined,
            session: undefined,
            accessToken: undefined,
          })
          throw error
        }
      },

      signInWithApple: async ({ identityToken, user }) => {
        set({ loading: true, error: undefined })
        try {
          const session = await AuthAPI.signInWithApple(identityToken, user)
          await get().completeSignIn(session)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Apple sign in failed'
          set({
            loading: false,
            error: message,
            user: undefined,
            session: undefined,
            accessToken: undefined,
          })
          throw error
        }
      },

      signOut: async () => {
        set({ loading: true })
        try {
          const { session } = get()
          if (session?.refreshToken) {
            await AuthAPI.signOut(session.refreshToken)
          }

          set({
            user: undefined,
            session: undefined,
            accessToken: undefined,
            loading: false,
            error: undefined,
          })

          // Clear persisted access token used by ApiClient
          clearPersistedAccessToken()
        } catch (error) {
          set({ loading: false })
          // Even if sign out fails on server, clear local data
          set({
            user: undefined,
            session: undefined,
            accessToken: undefined,
            error: undefined,
          })
          clearPersistedAccessToken()
        }
      },

      refreshSession: async () => {
        const currentSession = get().session
        if (!currentSession?.refreshToken) {
          throw new Error('Not authenticated')
        }

        const newSession = await AuthAPI.refreshToken(currentSession.refreshToken)
        const currentUser = get().user ?? buildFallbackUserFromAccessToken(newSession.accessToken)
        const sessionWithUser = { ...newSession, user: currentUser }

        set({
          user: currentUser,
          session: sessionWithUser,
          accessToken: newSession.accessToken,
          loading: false,
          error: undefined,
        })

        persistAccessToken(newSession.accessToken)
        void hydrateAuthenticatedUser(newSession.accessToken)
      },

      refreshUser: async () => {
        const { accessToken, session } = get()
        if (!accessToken) {
          throw new Error('Not authenticated')
        }

        try {
          const user = await AuthAPI.getCurrentUser(accessToken)
          set({
            user,
            session: session ? { ...session, user } : undefined,
          })
        } catch (error) {
          console.error('Failed to refresh user:', error)
          throw error
        }
      },

      syncUserProfile: (profile) => {
        const { user, session } = get()
        const mergedUser = mergeUserWithProfile(user ?? session?.user, profile)

        if (!mergedUser) {
          return
        }

        set({
          user: mergedUser,
          session: session ? { ...session, user: mergedUser } : undefined,
        })
      },

      clearError: () => {
        set({ error: undefined })
      },

      devLogin: () => {
        // Dev-only fake login
        const fakeUser: User = {
          id: 'dev-user-123',
          email: 'dev@certi.local',
          firstName: 'Dev',
          lastName: 'User',
          status: 'ACTIVE',
          roles: ['USER'],
          createdAt: new Date().toISOString(),
        }

        const fakeSession: Session = {
          accessToken: 'fake-access-token',
          refreshToken: 'fake-refresh-token',
          expiresIn: 3600,
          user: fakeUser,
        }

        set({
          user: fakeUser,
          session: fakeSession,
          accessToken: 'fake-access-token',
          loading: false,
          error: undefined,
        })

        console.log('🔧 Dev Login - Fake user logged in:', fakeUser)
      },

      isAdmin: () => {
        const { user } = get()
        return user?.roles?.includes('ADMIN') ?? false
      },

      _setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state, isHydrated: state })
      },
    }),
    {
      name: STORAGE_KEY,
      storage: zustandStorage,
      onRehydrateStorage: () => (state) => {
        // Sync accessToken from rehydrated session
        if (state?.session?.accessToken) {
          state.accessToken = state.session.accessToken
          persistAccessToken(state.session.accessToken)
          void hydrateAuthenticatedUser(state.session.accessToken)
        }
        state?._setHasHydrated(true)
      },
      partialize: (state) => ({
        user: state.user,
        session: state.session,
      }),
    }
  )
)

// Auto-refresh token logic
let refreshTimer: NodeJS.Timeout | null = null
const REFRESH_BUFFER_MS = 5 * 60 * 1000

const getSessionExpiryMs = (expiresIn: number) => {
  // The backend auth APIs return expiresIn in milliseconds. Keep a small
  // compatibility fallback for any older second-based values.
  return expiresIn > 0 && expiresIn < 86_400 ? expiresIn * 1000 : expiresIn
}

const setupTokenRefresh = () => {
  const state = useAuthStore.getState()

  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!state.session?.accessToken || !state.session?.refreshToken) {
    return
  }

  // Skip refresh for dev/fake tokens
  if (state.session.accessToken === 'fake-access-token') {
    return
  }

  // Check if token is expired or will expire soon (5 minutes buffer)
  const isExpired = AuthAPI.isTokenExpired(state.session.accessToken)
  const expiresInMs = getSessionExpiryMs(state.session.expiresIn)
  const refreshTime = isExpired ? 0 : Math.max(0, expiresInMs - REFRESH_BUFFER_MS)

  refreshTimer = setTimeout(async () => {
    try {
      const currentState = useAuthStore.getState()
      if (!currentState.session?.refreshToken) return

      const newSession = await AuthAPI.refreshToken(currentState.session.refreshToken)
      const sessionWithUser = { ...newSession, user: currentState.user }

      useAuthStore.setState({
        session: sessionWithUser,
        accessToken: newSession.accessToken,
      })

      // Keep localStorage token in sync for ApiClient
      persistAccessToken(newSession.accessToken)

      // Setup next refresh
      setupTokenRefresh()
    } catch (error) {
      console.error('Token refresh failed:', error)
      // Sign out user if refresh fails
      useAuthStore.getState().signOut()
    }
  }, refreshTime)
}

// Subscribe to auth state changes to setup token refresh
useAuthStore.subscribe((state) => {
  if (state.session?.accessToken) {
    setupTokenRefresh()
  } else if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
})

async function hydrateAuthenticatedUser(accessToken: string) {
  try {
    const user = await AuthAPI.getCurrentUser(accessToken)
    const currentState = useAuthStore.getState()

    if (currentState.session?.accessToken !== accessToken) {
      return
    }

    useAuthStore.setState({
      user,
      session: currentState.session ? { ...currentState.session, user } : undefined,
      error: undefined,
    })
  } catch (error) {
    console.warn('Failed to hydrate authenticated user profile:', error)
  }
}
